import {
  convertDimensionMagnitude,
  type DimensionUnit,
} from "../formats/dimensions";
import type { MainProductTypeSlug } from "../generated/product-types";

export const REQUIRED_LISTING_DIMENSIONS = [
  { name: "length", label: "Length" },
  { name: "width", label: "Width" },
  { name: "height", label: "Height" },
] as const;

export type RequiredListingDimensionName =
  (typeof REQUIRED_LISTING_DIMENSIONS)[number]["name"];

export interface ListingDimensions {
  length?: number;
  width?: number;
  height?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function firstPositiveNumber(
  name: RequiredListingDimensionName,
  source: unknown,
): number | undefined {
  const value = isRecord(source) ? source[name] : undefined;
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

export function listingDimensionsFromSource(
  source: unknown,
): ListingDimensions {
  const dimensions: ListingDimensions = {};
  for (const { name } of REQUIRED_LISTING_DIMENSIONS) {
    const value = firstPositiveNumber(name, source);
    if (value !== undefined) dimensions[name] = value;
  }
  return dimensions;
}

export function mergeListingDimensionSources(
  canonical: unknown,
  legacy: unknown,
): ListingDimensions {
  return {
    ...listingDimensionsFromSource(legacy),
    ...listingDimensionsFromSource(canonical),
  };
}

export function withoutListingDimensions<TRecord extends Record<string, unknown>>(
  source: TRecord,
): Omit<TRecord, "length" | "width" | "height" | "unit"> {
  const { length, width, height, unit, ...remaining } = source;
  void length;
  void width;
  void height;
  void unit;
  return remaining;
}

export function findMissingListingDimensions(
  source: unknown,
): RequiredListingDimensionName[] {
  const dimensions = listingDimensionsFromSource(source);
  return REQUIRED_LISTING_DIMENSIONS.flatMap(({ name }) => {
    return dimensions[name] === undefined ? [name] : [];
  });
}

const MINIMUM_SIDE_INCHES: Partial<Record<MainProductTypeSlug, number>> = {
  "metal-drums": 10,
  "plastic-drums": 10,
  "fiber-drums": 10,
  "ibc-totes": 24,
  "gaylord-boxes": 12,
  pallets: 3,
  "plastic-pallets": 3,
};

const DIMENSION_UNITS: readonly DimensionUnit[] = ["in", "mm", "cm", "ft"];

export interface UndersizedListingDimension {
  name: RequiredListingDimensionName;
  message: string;
}

function dimensionUnitOf(source: unknown): DimensionUnit {
  const unit = isRecord(source) ? source.unit : undefined;
  return DIMENSION_UNITS.find((candidate) => candidate === unit) ?? "in";
}

function minimumSideInches(productTypeSlug: string): number | undefined {
  return Object.entries(MINIMUM_SIDE_INCHES).find(
    ([slug]) => slug === productTypeSlug,
  )?.[1];
}

export function findUndersizedListingDimensions(
  productTypeSlug: string,
  source: unknown,
): UndersizedListingDimension[] {
  const minimumInches = minimumSideInches(productTypeSlug);
  if (minimumInches === undefined) return [];
  const unit = dimensionUnitOf(source);
  const tenths = convertDimensionMagnitude(minimumInches, "in", unit) * 10;
  const minimum = Math.ceil(Number(tenths.toPrecision(12))) / 10;
  const dimensions = listingDimensionsFromSource(source);
  return REQUIRED_LISTING_DIMENSIONS.flatMap(({ name, label }) => {
    const value = dimensions[name];
    return value !== undefined && value < minimum
      ? [{ name, message: `${label} must be at least ${minimum} ${unit}` }]
      : [];
  });
}
